import React, { useEffect, useState } from 'react';
import companyGeneralDataReducer from '../../redux/companyGeneralData';
import withReducers from '../../store/withReducers';
import useCompanyGeneralData from './hooks/useCompanyGeneralData';
import moment from 'moment';
import { AlertNotification, Button, Col, Label, LabeledCheckbox, LabeledRadioInButton, Loading, Row, Typography, XIcon } from '@commonsku/styles';
import Features from '../../constants/Features';
import { oauth } from '../../utils';

type AdminBillingRenewalDetailsProps = {
  onClose: () =>  void;
  identity: any;
}

function AdminBillingRenewalDetails({
  onClose,
  identity
}: AdminBillingRenewalDetailsProps) {
    const { data: { data }, loading: companyLoading, save } = useCompanyGeneralData();
    const [renewalFeatureId, setRenewalFeatureId] = useState<string | undefined>();
    const [renewalPaymentModel, setRenewalPaymentModel] = useState<string | undefined>();
    const [renewalLicenses, setRenewalLicenses] = useState<string | undefined>();
    const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
    const [stripeLoading, setStripeLoading] = useState<boolean>(true);
    const [contractedLicenses, setContractedLicenses] = useState<number | undefined>();
    const [activeUsers, setActiveUsers] = useState<number | undefined>();
    const [error, setError] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const dataLoading = companyLoading !== 'idle' || stripeLoading;

    useEffect(() => {
      if (data.renewal_feature_id && data.renewal_date) {
        setRenewalFeatureId(data.renewal_feature_id);
        setRenewalPaymentModel(data.renewal_payment_model);
        setRenewalLicenses(data.renewal_licenses);
      }
    }, [ data ]);

    useEffect(() => {
      oauth('GET', 'stripe', {}).then(
        ({ json }) => {
          setStripeLoading(false);
          const { stripe_info: stripeInfo, active_users: activeUsers } = json || {};
          if (!stripeInfo?.stripe_subscription_item) {
            setError(true);
            return;
          }

          setActiveUsers(activeUsers);
          setContractedLicenses(stripeInfo.stripe_subscription_item.quantity);
        },
        () => {
          setStripeLoading(false);
          setError(true);
        }
      );
    }, []);

    let title: string;
    let info: string;
    let terms: React.ReactNode;
    let featureLevelLabel: string | React.ReactNode;
    let renewalInfo: string;

    const nextRenewal = moment(data.renewal_date).format("MMM [1st] YYYY")
    title = `Your contract will automatically renew on ${nextRenewal}`;
    info = 'Use the settings below to set your license renewal preferences.';
    terms = <div>I'm authorized to make changes to our contract</div>;
    featureLevelLabel = 'Feature Level';
    renewalInfo = `You currently have ${contractedLicenses} contracted licenses and ${activeUsers === contractedLicenses ? 'all' : activeUsers} in use by assigned users.`;
    
    const onSave = async () => {
      if (saving) { return; }
      setSaving(true);
      try {
        await save({
          action: 'contract-changes',
          distributorId: data.tenant_id,
          renewalFeatureId,
          renewalPaymentModel,
          renewalLicenses,
        }, true);
        onClose();
        // No reset needed on success; onClose unmounts this component.
      } catch (e) {
        // Allow retry on failure so the user isn't stranded.
        setSaving(false);
      }
    };

    const message = error ? 'An error occurred during your account configuration, please contact commonsku support.' :
      'You are all set. Your account configuration has been updated.';

    const content = !dataLoading && error ? <>
      <p style={{margin: '65px 100px', fontSize: 24, textAlign: 'center'}}>
        {message}
      </p>
    </> : <>
      <AlertNotification
        href='https://help.commonsku.com/knowledge/essentials-advanced'
        learnMore={true}
        target={'_blank'}
      >
        {info}
      </AlertNotification>
      <Col style={{minHeight: 370}}>
        {dataLoading ? <Row pt={175} style={{minHeight: 395}}><Loading /></Row> : <>
          <Row mt={20}>
            <Label mb={5}>{featureLevelLabel}</Label>
            <LabeledRadioInButton label='Essentials' checked={renewalFeatureId === Features.DISTRIBUTOR_ESSENTIAL_FEATURE_ID} onChange={()=>setRenewalFeatureId(Features.DISTRIBUTOR_ESSENTIAL_FEATURE_ID)} />
            <LabeledRadioInButton label='Advanced' checked={renewalFeatureId === Features.DISTRIBUTOR_ADVANCED_FEATURE_ID} onChange={()=>setRenewalFeatureId(Features.DISTRIBUTOR_ADVANCED_FEATURE_ID)} />
          </Row>

          <Row mt={10}>
            <Label mb={5}>Would you like to pay annually (with a discount) or monthly?</Label>
            <LabeledRadioInButton label='Monthly' checked={renewalPaymentModel === 'MONTH'} onChange={()=>setRenewalPaymentModel('MONTH')} />
            <LabeledRadioInButton label='Annually 10% discount' checked={renewalPaymentModel === 'YEAR'} onChange={()=>setRenewalPaymentModel('YEAR')} />
          </Row>

          <Row mt={10}>
            <LabeledCheckbox
              label={'Remove unused licenses upon renewal'}
              checked={renewalLicenses === 'ACTIVE_USERS'}
              onChange={(e) => setRenewalLicenses(!e.target.checked ? 'CONTRACTED_LICENSES' : 'ACTIVE_USERS')}
            />
          </Row>

          <Row mt={10}>
            <p>{renewalInfo}</p>
          </Row>
          <Row mt={20}>
            <LabeledCheckbox
              label={terms}
              checked={acceptedTerms}
              onChange={()=>setAcceptedTerms(!acceptedTerms)}
            />
          </Row>
        </>}
      </Col>
    </>;

    return (
        <>
          <div style={{display: 'flex', justifyContent: 'space-between'}}>
            <Typography.H2 style={{marginBottom: 10}}>{title}</Typography.H2>
            <XIcon cursor={'pointer'} onClick={onClose} mt={2} ml={10} size='large' />
          </div>
          {content}
          <Col>
            <Row justify={'right'} mt={20}>
              {error &&
                <Button
                  variant={'primary'}
                  onClick={onClose}
                  disabled={dataLoading}
                >
                  Done
                </Button>
              }
              {!error &&
                <Button
                  onClick={() => onSave()}
                  disabled={!acceptedTerms || dataLoading || saving}
                >Save</Button>
              }
            </Row>
          </Col>
      </>
    );
}

export default withReducers(AdminBillingRenewalDetails, {
  companyGeneralData: companyGeneralDataReducer,
});
